import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Auth.css';
import './CharacterSelection.css';
import './Development.css';
import { authButtonStyle, authButtonSmallStyle } from '../constants/authStyles';
import AuthLayeredCard from './AuthLayeredCard';
import AuthSelectionToolbar from './AuthSelectionToolbar';
import { playPointIncreaseSound } from '../utils/buttonClickSound';
import {
  canPromoteSkill,
  canUpgradeSkillInDevelopment,
  formatSkillLevelForDevelopment,
} from '../utils/skillLevelUtils';
import { usePopupMessage } from '../hooks/usePopupMessage';
import { useCharacter } from '../context/CharacterContext';

const CLICK_DEBOUNCE_MS = 300;

const formatCooldown = (seconds) => {
  const total = Math.max(0, Number(seconds) || 0);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) return `${hours}s ${minutes}dk`;
  if (minutes > 0) return `${minutes}dk`;
  return `${total}s`;
};

const buildPublicImageUrl = (rawUrl) => {
  if (!rawUrl) return '';
  if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://') || rawUrl.startsWith('data:')) {
    return rawUrl;
  }
  return rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
};

// Sprite strip naming: e.g. 103s.png — not plain names ending in s (tas/elmas/gumus).
const isSpriteStripUrl = (rawUrl) => /\d+s\.png$/i.test(rawUrl || '');

const renderStatWithEfsunBonus = (baseValue, efsunBonus, storyBonus = 0, storyLabel = '') => (
  <>
    {baseValue}
    {efsunBonus > 0 && (
      <span className="dev-stat-efsun-bonus">+{efsunBonus}</span>
    )}
    {storyBonus !== 0 && (
      <span className="dev-stat-story-bonus" title={storyLabel}>
        {storyBonus > 0 ? '+' : ''}
        {storyBonus}
      </span>
    )}
  </>
);

// Hikâye görevlerinden gelen kalıcı etkiler (savaş ve arenada da aynen geçerli).
const StoryBonusSummary = ({ bonuses, t }) => {
  if (!bonuses) return null;
  const rows = [
    ['developmentHp', bonuses.vitality],
    ['developmentMp', bonuses.intelligence],
    ['developmentStrength', bonuses.strength],
    ['developmentAgility', bonuses.agility],
    ['developmentStoryDefense', bonuses.defense],
    ['developmentStoryMaxHp', bonuses.maxHpPercent, '%'],
    ['developmentStoryDamage', bonuses.damagePercent, '%'],
  ].filter(([, v]) => v);
  const titles = bonuses.titles || [];
  if (!rows.length && !titles.length) return null;
  return (
    <div className="dev-story-bonuses">
      <div className="dev-story-bonuses__title">{t('developmentStoryTitle')}</div>
      <div className="dev-story-bonuses__chips">
        {rows.map(([key, v, suffix]) => (
          <span key={key} className="dev-story-bonuses__chip">
            {t(key)} {v > 0 ? '+' : ''}
            {v}
            {suffix || ''}
          </span>
        ))}
        {titles.map((title) => (
          <span key={title} className="dev-story-bonuses__chip dev-story-bonuses__chip--title">
            {t('developmentStoryTitleLabel')}: {title}
          </span>
        ))}
      </div>
      <p className="dev-story-bonuses__note">{t('developmentStoryNote')}</p>
    </div>
  );
};

const DevelopmentStoneIcon = ({ imageUrl, alt, className = '', frameSize = '18px' }) => {
  if (!imageUrl) return <span className={`dev-stone-fallback-icon ${className}`.trim()}>?</span>;
  const src = buildPublicImageUrl(imageUrl);
  if (isSpriteStripUrl(imageUrl)) {
    return (
      <span
        className={`dev-stone-sprite-icon ${className}`.trim()}
        role="img"
        aria-label={alt}
        style={{ backgroundImage: `url(${src})`, '--sprite-frame-size': frameSize }}
      />
    );
  }
  return <img className={`dev-stone-sprite-icon ${className}`.trim()} src={src} alt={alt} />;
};

export const DevelopmentPanel = () => {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const { character: userCharacter, refreshCharacter } = useCharacter();
  const [userSkills, setUserSkills] = useState([]);
  const [activeStageStone, setActiveStageStone] = useState(null);
  const lastClickAtRef = useRef(0);
  const isProcessingRef = useRef(false);
  const { setError, setStatus } = usePopupMessage();
  const navigate = useNavigate();
  const username = localStorage.getItem('username');

  const fetchSkills = useCallback(async () => {
    try {
      const skillsResponse = await axios.get(
        `/api/development/user-skills?username=${username}`
      );
      if (skillsResponse.data.success) {
        setUserSkills(skillsResponse.data.skills || []);
      }
    } catch (err) {
      console.error('Error fetching skills:', err);
    }
  }, [username]);

  const fetchData = useCallback(async () => {
    await refreshCharacter().catch(() => {});
    await fetchSkills();
  }, [refreshCharacter, fetchSkills]);

  useEffect(() => {
    if (!username) {
      navigate('/login');
      return;
    }
    fetchSkills();
  }, [username, navigate, fetchSkills]);

  const handleIncreaseStat = async (statType) => {
    if (!userCharacter || userCharacter.statPoints <= 0) {
      setError('Yetersiz stat puanı!');
      return;
    }
    const now = Date.now();
    if (now - lastClickAtRef.current < CLICK_DEBOUNCE_MS) return;
    if (isProcessingRef.current) return;
    lastClickAtRef.current = now;
    isProcessingRef.current = true;
    playPointIncreaseSound();

    try {
      const response = await axios.post('/api/development/increase-stat', {
        username,
        statType,
      });

      if (response.data.success) {
        await fetchData();
      } else {
        setError(response.data.message || 'Hata oluştu');
      }
    } catch (err) {
      console.error('Error increasing stat:', err);
      setError(err.response?.data?.message || 'Hata oluştu');
    } finally {
      isProcessingRef.current = false;
    }
  };

  const handleIncreaseSkill = async (skillId) => {
    if (!userCharacter || userCharacter.skillPoints <= 0) {
      setError('Yetersiz skill puanı!');
      return;
    }
    const targetSkill = userSkills.find((skill) => skill.id === skillId);
    if (targetSkill && !canUpgradeSkillInDevelopment(targetSkill.level)) {
      setError('Usta seviyesindeki yetenekler gelişim ekranından yükseltilemez.');
      return;
    }
    const now = Date.now();
    if (now - lastClickAtRef.current < CLICK_DEBOUNCE_MS) return;
    if (isProcessingRef.current) return;
    lastClickAtRef.current = now;
    isProcessingRef.current = true;
    playPointIncreaseSound();

    try {
      const response = await axios.post('/api/development/increase-skill', {
        username,
        skillId,
      });

      if (response.data.success) {
        await fetchData();
      } else {
        setError(response.data.message || 'Hata oluştu');
      }
    } catch (err) {
      console.error('Error increasing skill:', err);
      setError(err.response?.data?.message || 'Hata oluştu');
    } finally {
      isProcessingRef.current = false;
    }
  };

  const handlePromoteSkill = async (skillId) => {
    const targetSkill = userSkills.find((skill) => skill.id === skillId);
    if (!targetSkill || !canPromoteSkill(targetSkill.level)) {
      setError('Bu yetenek daha fazla yükseltilemez.');
      return;
    }
    if ((targetSkill.cooldownRemaining || 0) > 0) {
      setError(`Bu skill için tekrar deneme ${formatCooldown(targetSkill.cooldownRemaining)} sonra yapılabilir.`);
      return;
    }
    const required = targetSkill.nextStageRequired || 1;
    const missingStageStone = (targetSkill.stageStones || []).find(
      (stageStone) => !stageStone.completed && !stageStone.hasEnough
    );
    if (missingStageStone) {
      setError(`${missingStageStone.name || 'Gerekli taş'} yetersiz.`);
      return;
    }
    if ((targetSkill.nextStageStoneCount || 0) < required) {
      setError(`${targetSkill.nextStageStoneName || 'Gerekli taş'} yetersiz.`);
      return;
    }

    const now = Date.now();
    if (now - lastClickAtRef.current < CLICK_DEBOUNCE_MS) return;
    if (isProcessingRef.current) return;
    lastClickAtRef.current = now;
    isProcessingRef.current = true;
    playPointIncreaseSound();

    try {
      const response = await axios.post('/api/development/promote-skill', {
        username,
        skillId,
      });

      if (response.data.success) {
        if (response.data.result && !response.data.result.successRoll) {
          setError(response.data.message || 'Yükseltme başarısız.');
        } else {
          setStatus(response.data.message || 'Yükseltme başarılı.');
        }
        await fetchData();
      } else {
        setError(response.data.message || 'Hata oluştu');
      }
    } catch (err) {
      console.error('Error promoting skill:', err);
      setError(err.response?.data?.message || 'Hata oluştu');
    } finally {
      isProcessingRef.current = false;
    }
  };

  const renderSkillCard = (skill) => {
    const canDevUpgrade = canUpgradeSkillInDevelopment(skill.level);
    const canPromote = skill.canPromote || canPromoteSkill(skill.level);
    const required = skill.nextStageRequired || 1;
    const stoneCount = skill.nextStageStoneCount || 0;
    const promotionStage = skill.promotionStage || 0;
    const totalStages = skill.totalStages || 1;
    const stageStones = skill.stageStones || [];
    const cooldownRemaining = skill.cooldownRemaining || 0;
    const hasAllRequiredStones = stageStones.length > 0
      ? stageStones.every((stageStone) => stageStone.completed || stageStone.hasEnough)
      : stoneCount >= required;
    const stageBoxes = Array.from({ length: totalStages }, (_, index) => (
      <span
        key={index}
        className={[
          'dev-stage-box',
          index < promotionStage ? 'filled' : '',
          index === promotionStage ? 'active' : '',
          index > promotionStage ? 'disabled' : '',
        ].filter(Boolean).join(' ')}
        aria-label={`Kademe ${index + 1}${
          index < promotionStage
            ? ' dolu'
            : index === promotionStage
              ? ' aktif'
              : ' kilitli'
        }`}
      />
    ));

    return (
      <div key={skill.id} className="dev-stat-item dev-skill-item">
        <div className="dev-stat-label">{skill.skillName}</div>
        <div className="dev-stat-value">
          {formatSkillLevelForDevelopment(skill.level)}
        </div>

        {canPromote ? (
          <>
            <div className="dev-skill-promotion-info">
              <span>Kademe {promotionStage + 1}/{totalStages}</span>
              <div className="dev-stage-boxes" aria-hidden="true">
                {stageBoxes}
              </div>
              <span>{skill.promotionSuccessRate || 30}% şans</span>
              {cooldownRemaining > 0 && (
                <span className="dev-skill-cooldown">
                  Tekrar deneme: {formatCooldown(cooldownRemaining)}
                </span>
              )}
              <div className="dev-stage-stones">
                {stageStones.length > 0 ? stageStones.map((stageStone) => (
                  <button
                    type="button"
                    key={`${skill.id}-${stageStone.stage}`}
                    className={[
                      'dev-stage-stone',
                      stageStone.completed ? 'completed' : '',
                      stageStone.active ? 'active' : '',
                      stageStone.locked ? 'locked' : '',
                      !stageStone.completed && !stageStone.hasEnough ? 'missing' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => setActiveStageStone({
                      ...stageStone,
                      skillName: skill.skillName,
                      successRate: skill.promotionSuccessRate || 30,
                    })}
                    title={stageStone.name}
                  >
                    <DevelopmentStoneIcon
                      imageUrl={stageStone.imageUrl}
                      alt={stageStone.name}
                      className="dev-stone-sprite-icon--large"
                      frameSize="44px"
                    />
                    <span className="dev-stage-stone-count">
                      {stageStone.count}/{stageStone.totalRequired || stageStone.required}
                    </span>
                  </button>
                )) : (
                  <span>
                    {skill.nextStageStoneName || 'Taş'}: {stoneCount}/{required}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              className="auth-button dev-promote-button"
              style={authButtonSmallStyle}
              data-silent-click="true"
              onClick={() => handlePromoteSkill(skill.id)}
              disabled={!hasAllRequiredStones || cooldownRemaining > 0}
            >
              {t('developmentPromote')}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="auth-button dev-stat-plus"
              style={authButtonSmallStyle}
              data-silent-click="true"
              onClick={() => handleIncreaseSkill(skill.id)}
              disabled={
                !userCharacter.skillPoints ||
                userCharacter.skillPoints <= 0 ||
                !canDevUpgrade
              }
            >
              +
            </button>
            {!canDevUpgrade && (
              <div className="dev-skill-promotion-info">Perfect</div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      {userCharacter && (
        <div className="auth-selection-body dev-body">
            <section className="dev-section">
              <h2 className="auth-selection-section-title">
                Level {userCharacter.level}
              </h2>
              <div className="dev-points-info">
                <span>{t('developmentStatPoints', { count: userCharacter.statPoints || 0 })}</span>
                <span>{t('developmentSkillPoints', { count: userCharacter.skillPoints || 0 })}</span>
              </div>
            </section>

            <section className="dev-section">
              <h2 className="auth-selection-section-title">{t('developmentStatsTitle')}</h2>
              <div className="dev-stats-grid">
                {(() => {
                  const eqBonus = userCharacter.equipmentStatBonuses || {};
                  const story = userCharacter.storyBonuses || {};
                  const storyLabel = t('developmentStoryTitle');
                  return (
                    <>
                <div className="dev-stat-item">
                  <div className="dev-stat-label">{t('developmentHp')}</div>
                  <div className="dev-stat-value">
                    {renderStatWithEfsunBonus(
                      userCharacter.hp || 5,
                      (userCharacter.efsunVitalityBonus || 0) + (eqBonus.vitBonus || 0),
                      story.vitality || 0,
                      storyLabel
                    )}
                  </div>
                  <button
                    type="button"
                    className="auth-button dev-stat-plus"
                    style={authButtonSmallStyle}
                    data-silent-click="true"
                    onClick={() => handleIncreaseStat('hp')}
                    disabled={
                      !userCharacter.statPoints || userCharacter.statPoints <= 0
                    }
                  >
                    +
                  </button>
                </div>
                <div className="dev-stat-item">
                  <div className="dev-stat-label">{t('developmentMp')}</div>
                  <div className="dev-stat-value">
                    {renderStatWithEfsunBonus(
                      userCharacter.mpStat ?? userCharacter.mp ?? 5,
                      (userCharacter.efsunIntelligenceBonus || 0) + (eqBonus.intBonus || 0),
                      story.intelligence || 0,
                      storyLabel
                    )}
                  </div>
                  <button
                    type="button"
                    className="auth-button dev-stat-plus"
                    style={authButtonSmallStyle}
                    data-silent-click="true"
                    onClick={() => handleIncreaseStat('mp')}
                    disabled={
                      !userCharacter.statPoints || userCharacter.statPoints <= 0
                    }
                  >
                    +
                  </button>
                </div>
                <div className="dev-stat-item">
                  <div className="dev-stat-label">{t('developmentStrength')}</div>
                  <div className="dev-stat-value">
                    {renderStatWithEfsunBonus(
                      userCharacter.strength || 5,
                      (userCharacter.efsunStrengthBonus || 0) + (eqBonus.strengthBonus || 0),
                      story.strength || 0,
                      storyLabel
                    )}
                  </div>
                  <button
                    type="button"
                    className="auth-button dev-stat-plus"
                    style={authButtonSmallStyle}
                    data-silent-click="true"
                    onClick={() => handleIncreaseStat('strength')}
                    disabled={
                      !userCharacter.statPoints || userCharacter.statPoints <= 0
                    }
                  >
                    +
                  </button>
                </div>
                <div className="dev-stat-item">
                  <div className="dev-stat-label">{t('developmentAgility')}</div>
                  <div className="dev-stat-value">
                    {renderStatWithEfsunBonus(
                      userCharacter.agility || 5,
                      (userCharacter.efsunAgilityBonus || 0) + (eqBonus.agilityBonus || 0),
                      story.agility || 0,
                      storyLabel
                    )}
                  </div>
                  <button
                    type="button"
                    className="auth-button dev-stat-plus"
                    style={authButtonSmallStyle}
                    data-silent-click="true"
                    onClick={() => handleIncreaseStat('agility')}
                    disabled={
                      !userCharacter.statPoints || userCharacter.statPoints <= 0
                    }
                  >
                    +
                  </button>
                </div>
                    </>
                  );
                })()}
              </div>
              <StoryBonusSummary bonuses={userCharacter.storyBonuses} t={t} />
            </section>

            <section className="dev-section">
              <h2 className="auth-selection-section-title">{t('developmentSkillsTitle')}</h2>
              {userCharacter.level < 5 ? (
                <>
                  <p className="auth-info-text">
                    {t('developmentSkillsLocked')}
                  </p>
                  <div className="selection-grid dev-skills-grid">
                    {userSkills.map((skill) => (
                      <div
                        key={skill.id}
                        className="selection-card locked"
                        title={t('developmentSkillsLocked')}
                      >
                        <h3>{skill.skillName}</h3>
                        <p>{formatSkillLevelForDevelopment(skill.level)}</p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <p className="auth-info-text">
                    1-16 arası skill puanı ile, M1 sonrasında taş ile yükseltin.
                  </p>
                  <div className="dev-stats-grid dev-skills-grid">
                    {userSkills.map((skill) => renderSkillCard(skill))}
                  </div>
                </>
              )}
            </section>
          </div>
      )}
      {activeStageStone && (
        <div
          className="dev-stone-popup-backdrop"
          role="presentation"
          onClick={() => setActiveStageStone(null)}
        >
          <div
            className="dev-stone-popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dev-stone-popup-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="dev-stone-popup-close"
              onClick={() => setActiveStageStone(null)}
              aria-label={tc('close')}
            >
              ×
            </button>
            <DevelopmentStoneIcon
              imageUrl={activeStageStone.imageUrl}
              alt={activeStageStone.name}
              className="dev-stone-popup-icon"
              frameSize="100px"
            />
            <h3 id="dev-stone-popup-title">{activeStageStone.name}</h3>
            <p>{activeStageStone.skillName} yükseltmesi için kullanılır.</p>
            <p>Kademe: {activeStageStone.stage}</p>
            <p>Envanter: {activeStageStone.count}/{activeStageStone.totalRequired || activeStageStone.required}</p>
            <p>Başarı şansı: %{activeStageStone.successRate}</p>
          </div>
        </div>
      )}
    </>
  );
};

const Development = () => {
  const { t } = useTranslation('inventory');
  const navigate = useNavigate();

  return (
    <div className="auth-container auth-container--selection">
      <AuthLayeredCard>
        <AuthSelectionToolbar
          onBack={() => navigate('/game')}
          title={t('developmentTitle')}
          subtitle={t('developmentSubtitle')}
          buttonStyle={authButtonStyle}
        />
        <DevelopmentPanel />
      </AuthLayeredCard>
    </div>
  );
};

export default Development;
